// SECTOR DEITY — the SHAPE deity of whichever sector is active on the compass / mountain
// (Joel, 2026-10-17). Its face sits in the top bar beside SyQi's butterfly; tapping it drops
// four modes:
//   Call  — voice only, the standard talking overlay's phone mode.
//   Video — the deity stands in the lower-left corner (3/4 of SyQi's box) and speaks its
//           sector overview; the chat box (where the node placard sits) shows what it says
//           and takes the user's replies. The deity has full read/write in its sector
//           (deity-config.ts), so "move X to Friday" or "mark Y done" really happens.
//   Chat  — the same box, text only (no figure, no voice).
//   Play  — every sector deity in turn, starting with the active one, speaks its overview
//           (projects + tasks tagged with the sector, and their progress).
// The sector follows the compass pick / mountain face; changing it swaps the deity.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { MessageSquare, Pause, Phone, Play, Send, SkipBack, SkipForward, Square, Video, X } from 'lucide-react-native';
import type { Sector } from '../domain/olmps';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import { askAgent } from '../live/askAgent';
import type { DayEntry } from '../olmps/useTodayData';
import { fetchSectorBrief } from '../live/sectorBriefApi';
import { sectorAudio as councilAudio, type PlaybackState, type SpeakItem } from '../council/councilAudio';
import { SECTORS } from './orbit';
import { PLACARD_BOTTOM, setPlacardDock } from './NodePlacard';
import { buildSectorBrief, useSectorTasks } from './sectorBrief';
import { SHAPE_DEITIES, shapeFullFor, type ShapeDeity } from './shapeRoster';
import { openTalk, SpeakerFigure } from './TalkingOverlay';
import { useShapeSeats } from './useShapeSeats';
import { SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

/** 'overview' = the sector overview sheet (tap a sector title on the compass): the deity reads its rundown aloud
 *  and the sheet's chat box takes replies. */
export type SectorDeityMode = 'video' | 'chat' | 'play' | 'overview';

/** The corner figure's box: head to waist on the bottom edge, at most 3/4 of SyQi's box, 38% of the width,
 *  30% of the height. The home lays the compass out above it. */
export function deityFigureSize(W: number, H: number): { figW: number; figH: number } {
  const sayH = H * 0.62;
  const figH = sayH * Math.min(0.75, (W * 0.38) / (sayH * 0.8), (H * 0.3) / sayH);
  return { figH, figW: figH * 0.8 };
}
type Line = { role: 'user' | 'deity' | 'done'; text: string };

/** The sector's seat deity (its Endeavour's deity in ATLAS), else the sector's first SHAPE deity. */
function deityFor(sector: Sector, seats: Record<Sector, ShapeDeity | undefined>): ShapeDeity {
  return seats[sector] ?? SHAPE_DEITIES.find((d) => d.sector === sector)!;
}

/** Speak one rundown (sentence-chunked, so it starts fast) and resolve when it ends or is stopped. */
function speakAndWait(item: SpeakItem): Promise<void> {
  return new Promise((resolve) => {
    let started = false;
    let unsub: (() => void) | null = null;
    unsub = councilAudio.subscribe((st: PlaybackState) => {
      if (st.id === item.id) started = true;
      else if (started) {
        unsub?.();
        resolve();
      }
    });
    councilAudio.playNow(item, { chunked: true });
  });
}

/** Short stable hash (audio cache key for a rundown's text). */
function hashText(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function useSectorDeity({
  userId,
  sector,
  spine,
  projects,
  projectsAreDemo,
  dayEntries,
  visible,
  onShowCompass,
  onSector,
}: {
  userId: string | undefined;
  /** The active sector (compass pick / mountain face). */
  sector: Sector;
  spine: OracleSpine | null;
  projects: Record<Sector, OracleProject[]>;
  projectsAreDemo: boolean;
  /** Today's plan (agenda, scheduled tasks, events): items tied to a sector go into its readout. */
  dayEntries: DayEntry[];
  /** The compass screen is showing (the stage only draws there). */
  visible: boolean;
  onShowCompass: () => void;
  /** Play turned to a sector (the mountain snaps its face to it). */
  onSector?: (s: Sector) => void;
}) {
  const seats = useShapeSeats(userId, true);
  const tasks = useSectorTasks(userId);
  const [mode, setMode] = useState<SectorDeityMode | null>(null);
  const [menuOpen, setMenuOpenState] = useState(false);
  /** The sector being spoken for when it isn't the active one: Play's current sector, kept if the user talks back. */
  const [focus, setFocus] = useState<Sector | null>(null);
  const [playIdx, setPlayIdx] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);
  const [sending, setSending] = useState(false);
  const [briefing, setBriefing] = useState(false);
  const [audio, setAudio] = useState<PlaybackState>(councilAudio.current);
  const conv = useRef<string | undefined>(undefined);
  const playToken = useRef(0);
  /** Bumped whenever a new opening rundown starts, so a late one never lands on a newer scene. */
  const briefToken = useRef(0);
  /** Set when Play hands over to Video mid-round: keep the conversation instead of re-briefing. */
  const keepLines = useRef(false);
  useEffect(() => councilAudio.subscribe(setAudio), []);

  const shown = focus ?? sector;
  const deity = deityFor(shown, seats);

  /** Offline fallback only: the data readout (used when the rundown service can't be reached). */
  const localBrief = (s: Sector) =>
    buildSectorBrief({
      sector: s,
      deityName: deityFor(s, seats).name,
      node: spine?.[s],
      projects: projectsAreDemo ? [] : projects[s] ?? [],
      tasks: tasks[s] ?? [],
      today: todayFor(s),
    });
  /** An item on today's plan belongs to a sector by its sector tag, its TKNE project, its task, or its name. */
  const todayFor = (s: Sector) => {
    const designIds = new Set((projectsAreDemo ? [] : projects[s] ?? []).map((p) => p.id));
    const taskIds = new Set((tasks[s] ?? []).map((x) => x.id));
    const label = SECTOR_NAME[s].toLowerCase();
    return dayEntries.filter(
      (e) =>
        e.sector === s ||
        (!!e.designId && designIds.has(e.designId)) ||
        (!!e.taskId && taskIds.has(e.taskId)) ||
        (!e.sector && !e.designId && !e.taskId && e.title.toLowerCase().includes(label)),
    );
  };
  /** Today's rundown for a sector: the deity's staff-meeting readout (server-cached per day, deduped in flight). */
  const rundown = (s: Sector): Promise<string> =>
    fetchSectorBrief(s, deityFor(s, seats).id)
      .then((r) => r.text)
      .catch(() => localBrief(s));
  const rundownRef = useRef(rundown);
  rundownRef.current = rundown;
  /** Same text → same audio id, so a prefetch and the real play share the downloaded clips. */
  const speakItem = (s: Sector, text: string) => {
    const id = deityFor(s, seats).id;
    return { id: `brief-${id}-${hashText(text)}`, speaker: id, text };
  };
  const speakItemRef = useRef(speakItem);
  speakItemRef.current = speakItem;

  // Warm the active sector's rundown text while the compass is showing, so a pick starts fast.
  useEffect(() => {
    if (!visible || !userId) return;
    const id = setTimeout(() => void rundownRef.current(sector), 1200);
    return () => clearTimeout(id);
  }, [visible, userId, sector, seats[sector]?.id]);

  /** Opening the menu is the intent signal: start the first audio clips downloading. */
  const setMenuOpen = (next: boolean | ((o: boolean) => boolean)) => {
    setMenuOpenState((o) => {
      const v = typeof next === 'function' ? next(o) : next;
      if (v && !o) void rundownRef.current(shown).then((text) => councilAudio.prefetch(speakItemRef.current(shown, text), { chunked: true }));
      return v;
    });
  };

  const stopPlay = () => {
    playToken.current++;
    setFocus(null);
    councilAudio.stop();
  };
  const close = () => {
    stopPlay();
    briefToken.current++;
    setBriefing(false);
    setMode(null);
    setLines([]);
    conv.current = undefined;
  };

  // Video / Chat: the active sector's deity opens with today's rundown; a new sector swaps deity and starts over.
  useEffect(() => {
    if (mode !== 'video' && mode !== 'chat') return;
    if (keepLines.current) {
      keepLines.current = false;
      return;
    }
    const s = sector;
    const token = ++briefToken.current;
    setFocus(null);
    conv.current = undefined;
    setLines([]);
    setBriefing(true);
    councilAudio.stop();
    void rundownRef.current(s).then((text) => {
      if (token !== briefToken.current) return;
      setBriefing(false);
      setLines([{ role: 'deity', text }]);
      if (mode === 'video') councilAudio.playNow(speakItemRef.current(s, text), { chunked: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sector, seats[sector]?.id]);

  // Overview: the tapped sector's deity reads today's rundown aloud; the sheet shows it and takes replies.
  useEffect(() => {
    if (mode !== 'overview' || !focus) return;
    const s = focus;
    const token = ++briefToken.current;
    conv.current = undefined;
    setLines([]);
    setBriefing(true);
    councilAudio.stop();
    void rundownRef.current(s).then((text) => {
      if (token !== briefToken.current) return;
      setBriefing(false);
      setLines([{ role: 'deity', text }]);
      councilAudio.playNow(speakItemRef.current(s, text), { chunked: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, focus, seats[focus ?? sector]?.id]);

  /** Open the sector overview sheet on `s` (tap on a compass sector title). */
  const openOverview = (s: Sector) => {
    setMenuOpenState(false);
    playToken.current++;
    councilAudio.stop();
    setFocus(s);
    setMode('overview');
  };

  // Leaving the compass screen ends the scene.
  useEffect(() => {
    if (visible) return;
    setMenuOpenState(false);
    if (mode) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const jumpRef = useRef<number | null>(null);
  const playIdxRef = useRef(0);
  const skipWake = useRef<(() => void) | null>(null);
  const skipWait = () => new Promise<null>((r) => (skipWake.current = () => r(null)));
  /** Play all: previous (-1) / next (+1) deity. Previous on the first one replays it. */
  const skip = (dir: 1 | -1) => {
    if (mode !== 'play') return;
    jumpRef.current = Math.max(0, playIdxRef.current + dir);
    councilAudio.stop();
    skipWake.current?.();
  };

  const play = async () => {
    const token = ++playToken.current;
    briefToken.current++;
    setMode('play');
    conv.current = undefined;
    const start = SECTORS.indexOf(sector);
    const order = SECTORS.map((_, i) => SECTORS[(start + i) % SECTORS.length]);
    // All five rundowns start generating at once; each deity speaks as soon as its turn comes.
    const texts = order.map((s) => rundownRef.current(s));
    // ◀ / ▶ skip: `jump` holds the index to go to; `skipWake` interrupts whatever the round is waiting on.
    let i = 0;
    while (i < order.length) {
      const s = order[i];
      if (token !== playToken.current) return;
      jumpRef.current = null;
      playIdxRef.current = i;
      setFocus(s);
      setPlayIdx(i);
      onSector?.(s);
      setLines([]);
      setBriefing(true);
      const text = await Promise.race([texts[i], skipWait()]);
      if (token !== playToken.current) return;
      if (text != null && jumpRef.current == null) {
        setBriefing(false);
        setLines([{ role: 'deity', text }]);
        const nxt = order[i + 1];
        if (nxt) void texts[i + 1].then((t) => councilAudio.prefetch(speakItemRef.current(nxt, t), { chunked: true }));
        await Promise.race([speakAndWait(speakItemRef.current(s, text)), skipWait()]);
        if (token !== playToken.current) return;
      }
      i = jumpRef.current ?? i + 1;
    }
    if (token !== playToken.current) return;
    setFocus(null);
    setMode(null);
    setLines([]);
  };

  const pick = (m: 'call' | SectorDeityMode) => {
    setMenuOpen(false);
    if (m === 'call') {
      close();
      openTalk(deity.id, deity.name, 'audio');
      return;
    }
    if (!visible) onShowCompass();
    if (m === 'play') {
      if (mode === 'play') close();
      else void play();
      return;
    }
    stopPlay();
    setMode(m);
  };

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || sending) return;
    if (mode === 'play') {
      // Talking back mid-round: stop the round and keep talking with whoever was speaking (focus stays).
      playToken.current++;
      councilAudio.stop();
      keepLines.current = true;
      setMode('video');
    }
    const d = deity;
    setLines((p) => [...p, { role: 'user', text }]);
    setSending(true);
    try {
      const r = await askAgent(d.id, `[${SECTOR_NAME[shown]} sector] ${text}`, conv.current);
      if (r.conversationId) conv.current = r.conversationId;
      const reply = r.text || 'Done.';
      setLines((p) => [...p, { role: 'deity', text: reply }, ...r.applied.map((x) => ({ role: 'done' as const, text: x }))]);
      if (mode !== 'chat') councilAudio.playNow({ id: `reply-${d.id}-${Date.now()}`, speaker: d.id, text: reply }, { chunked: true });
    } catch (e) {
      setLines((p) => [...p, { role: 'deity', text: `That request failed (${e instanceof Error ? e.message : 'unknown error'}). Nothing was changed. Try again?` }]);
    } finally {
      setSending(false);
    }
  };

  return { figureOn: visible && (mode === 'video' || mode === 'play'), deity, sector: shown, mode, menuOpen, setMenuOpen, pick, close, send, lines, sending, briefing, audio, playing: mode === 'play', playIdx, skip, visible, openOverview, tasks, todayFor };
}
export type SectorDeityCtl = ReturnType<typeof useSectorDeity>;

/** A SHAPE deity's head in a gold ring, cropped from its standing figure. */
export function DeityFace({ id, size = 38 }: { id: string; size?: number }) {
  const t = useV10();
  const full = shapeFullFor(id);
  // The standing figures are ~1:2.4; the head is roughly the top seventh.
  const h = size * 5.6;
  const w = full ? h * full.aspect : 0;
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden', borderWidth: 1.5, borderColor: t.gold, backgroundColor: 'rgba(16,24,48,0.92)', alignItems: 'center' }}>
      {full ? <Image source={full.src} resizeMode="cover" style={{ width: w, height: h, marginTop: size * 0.02 }} /> : null}
    </View>
  );
}

/** Top-bar button (beside SyQi's butterfly) + its mode dropdown. */
export function SectorDeityButton({ ctl }: { ctl: SectorDeityCtl }) {
  const t = useV10();
  const styles = useStyles();
  const { deity, sector, menuOpen, setMenuOpen, pick, mode } = ctl;
  const row = (key: 'call' | SectorDeityMode, icon: React.ReactNode, label: string, on = false) => (
    <Pressable key={key} onPress={() => pick(key)} style={[styles.menuRow, on && styles.menuRowOn]} accessibilityLabel={`${label} ${deity.name}`}>
      {icon}
      <Text style={[styles.menuText, on && { color: t.goldBright }]}>{label}</Text>
    </Pressable>
  );
  return (
    <View>
      <Pressable onPress={() => setMenuOpen((o) => !o)} hitSlop={8} accessibilityLabel={`${deity.name}, ${SECTOR_NAME[sector]} deity`} style={[styles.faceBtn, (menuOpen || mode) && { boxShadow: `0 0 12px ${t.goldGlow}` } as object]}>
        <DeityFace id={deity.id} size={38} />
        <View style={[styles.sectorPip, { backgroundColor: SECTOR_COLOR[sector] }]} />
      </Pressable>
      {menuOpen ? (
        <View style={styles.menu}>
          <Text style={styles.menuHead} numberOfLines={1}>
            {deity.name} · {SECTOR_NAME[sector]}
          </Text>
          {row('call', <Phone size={16} color={t.ivory} />, 'Call')}
          {row('video', <Video size={16} color={mode === 'video' ? t.goldBright : t.ivory} />, 'Video', mode === 'video')}
          {row('chat', <MessageSquare size={16} color={mode === 'chat' ? t.goldBright : t.ivory} />, 'Chat', mode === 'chat')}
          {row(
            'play',
            mode === 'play' ? <Square size={15} color={t.goldBright} /> : <Play size={16} color={t.ivory} />,
            mode === 'play' ? 'Stop' : 'Play all',
            mode === 'play',
          )}
        </View>
      ) : null}
    </View>
  );
}

/** The deity in the screen's lower-left corner, a message button beside it, and the chat box (the node
 *  placard's spot) that the button opens — closed by default so the whole compass stays visible.
 *  Chat mode has no figure: the box opens straight away. Compass screen only. */
export function SectorDeityStage({ ctl }: { ctl: SectorDeityCtl }) {
  const t = useV10();
  const styles = useStyles();
  const { width: W, height: H } = useWindowDimensions();
  const { deity, sector, mode, lines, sending, briefing, send, close, audio, playing, playIdx, skip, visible } = ctl;
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<ScrollView | null>(null);
  const boxW = Math.min(360, W - 24);
  const boxH = Math.round(Math.min(H * 0.3, 270));
  const figure = mode === 'video' || mode === 'play';
  const [boxOpen, setBoxOpen] = useState(false);
  useEffect(() => setBoxOpen(mode === 'chat'), [mode]);
  const { figH, figW } = deityFigureSize(W, H);
  const figScale = figH / (H * 0.62);
  // The node placard rises above the figure, and hides while the chat box is open (never stacked).
  useEffect(() => {
    if (!mode || !visible || mode === 'overview') setPlacardDock(null);
    else setPlacardDock({ bottom: figure ? Math.max(PLACARD_BOTTOM, figH + 8) : PLACARD_BOTTOM, hidden: boxOpen });
  }, [mode, visible, figure, figH, boxOpen]);
  useEffect(() => () => setPlacardDock(null), []);
  // The open box sits right of the deity's head (never over her face), else centred like the placard.
  const boxLeft = Math.max(figure ? figW * 0.72 : 0, (W - boxW) / 2);
  const status = useMemo(() => {
    if (sending) return 'Thinking…';
    if (briefing) return 'Preparing today’s rundown…';
    if (audio.id && audio.loading) return 'Getting ready…';
    if (audio.id) return 'Speaking';
    return '';
  }, [sending, briefing, audio.id, audio.loading]);
  if (!mode || !visible || mode === 'overview') return null;
  const submit = () => {
    const v = draft;
    setDraft('');
    void send(v);
  };
  return (
    <View pointerEvents="box-none" style={[StyleSheet.absoluteFill, { zIndex: 28 }]}>
      {figure ? (
        <>
          <View pointerEvents="none" style={{ position: 'absolute', left: 0, bottom: 0 }}>
            <SpeakerFigure key={deity.id} deityId={deity.id} hallOnly scale={figScale} speaking={!!audio.id && !audio.loading} />
          </View>
          <View pointerEvents="box-none" style={[styles.side, { left: figW * 0.74, bottom: 12 }]}>
            <Pressable onPress={close} hitSlop={6} style={styles.end} accessibilityLabel={`Dismiss ${deity.name}`}>
              <X size={14} color={t.ivory} />
            </Pressable>
            {playing ? (
              <Pressable onPress={() => skip(-1)} hitSlop={4} style={styles.skipBtn} accessibilityLabel="Previous deity">
                <SkipBack size={15} color={t.goldBright} />
              </Pressable>
            ) : null}
            {audio.id || playing ? (
              <Pressable onPress={() => void councilAudio.togglePause()} hitSlop={6} style={[styles.pause, audio.paused && styles.msgOn]} accessibilityLabel={audio.paused ? `Resume ${deity.name}` : `Pause ${deity.name}`}>
                {audio.paused ? <Play size={17} color={t.onGold} /> : <Pause size={17} color={t.goldBright} />}
              </Pressable>
            ) : null}
            {playing ? (
              <Pressable onPress={() => skip(1)} hitSlop={4} style={styles.skipBtn} accessibilityLabel="Next deity">
                <SkipForward size={15} color={t.goldBright} />
              </Pressable>
            ) : null}
            <Pressable
              onPress={() => setBoxOpen((o) => !o)}
              hitSlop={6}
              style={[styles.msg, boxOpen && styles.msgOn, !!audio.id && !audio.loading && ({ boxShadow: `0 0 16px ${t.goldGlow}` } as object)]}
              accessibilityLabel={boxOpen ? `Hide ${deity.name}'s chat` : `Chat with ${deity.name}`}
            >
              <MessageSquare size={19} color={boxOpen ? t.onGold : t.goldBright} />
            </Pressable>
          </View>
        </>
      ) : null}
      {boxOpen ? (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={[styles.box, { width: Math.min(boxW, W - boxLeft - 10), left: boxLeft, bottom: figure ? Math.max(PLACARD_BOTTOM, playing ? 252 : 152) : PLACARD_BOTTOM, height: boxH }]}
      >
        <View style={styles.head}>
          <DeityFace id={deity.id} size={30} />
          <View style={{ flex: 1 }}>
            <Text style={styles.kick} numberOfLines={1}>
              {SECTOR_NAME[sector]}
              {playing ? ` · ${playIdx + 1} of ${SECTORS.length}` : ''}
              {status ? ` · ${status}` : ''}
            </Text>
            <Text style={styles.name} numberOfLines={1}>
              {deity.name}
            </Text>
          </View>
          <Pressable onPress={figure ? () => setBoxOpen(false) : close} hitSlop={8} style={styles.x} accessibilityLabel={figure ? `Hide ${deity.name}'s chat` : `Close ${deity.name}`}>
            <X size={16} color={t.ivory} />
          </Pressable>
        </View>
        <ScrollView ref={scrollRef} style={{ flex: 1 }} contentContainerStyle={{ gap: 6, paddingVertical: 4 }} onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })} showsVerticalScrollIndicator={false}>
          {lines.map((l, i) =>
            l.role === 'done' ? (
              <Text key={i} style={styles.done}>
                ✓ {l.text}
              </Text>
            ) : (
              <View key={i} style={[styles.bubble, l.role === 'user' ? styles.mine : styles.theirs]}>
                <Text style={[styles.bubbleText, l.role === 'user' && { color: t.onGold }]}>{l.text.replace(/\*\*|__/g, '')}</Text>
              </View>
            ),
          )}
          {sending || briefing ? (
            <View style={[styles.bubble, styles.theirs]}>
              <Text style={styles.bubbleText}>…</Text>
            </View>
          ) : null}
        </ScrollView>
        <View style={styles.inputRow}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={submit}
            returnKeyType="send"
            placeholder={`Tell ${deity.name} what to change…`}
            placeholderTextColor="#6b7280"
            style={styles.input}
          />
          <Pressable onPress={submit} style={styles.send} accessibilityLabel="Send">
            <Send size={16} color={t.onGold} />
          </Pressable>
        </View>
      </KeyboardAvoidingView>
      ) : null}
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    faceBtn: { width: 38, height: 38, borderRadius: 19 },
    sectorPip: { position: 'absolute', right: -1, bottom: -1, width: 11, height: 11, borderRadius: 6, borderWidth: 1.5, borderColor: t.night },
    menu: { position: 'absolute', top: 46, right: -40, width: 158, padding: 6, gap: 2, borderRadius: 16, backgroundColor: 'rgba(9,14,32,0.96)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.5)', zIndex: 40, boxShadow: '0 8px 24px rgba(0,0,0,0.45)' } as object,
    menuHead: { color: t.goldBright, fontSize: 10, fontWeight: '800', letterSpacing: 1.2, textTransform: 'uppercase', paddingHorizontal: 8, paddingTop: 4, paddingBottom: 4 },
    menuRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 10, height: 38, borderRadius: 10, outlineStyle: 'none' } as object,
    menuRowOn: { backgroundColor: 'rgba(214,180,106,0.18)' },
    menuText: { color: t.ivory, fontSize: 14, fontWeight: '700' },
    box: { position: 'absolute', padding: 12, gap: 8, borderRadius: 16, backgroundColor: 'rgba(9,14,32,0.92)', borderWidth: 1, borderColor: t.gold, boxShadow: `0 8px 24px ${t.glowSoft}` } as object,
    head: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    kick: { fontSize: 10.5, fontWeight: '800', letterSpacing: 1.4, color: t.goldBright, textTransform: 'uppercase' },
    name: { fontSize: 15, fontWeight: '800', letterSpacing: 1.2, color: t.ivory },
    side: { position: 'absolute', alignItems: 'center', gap: 8 },
    end: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.85)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.5)' },
    msg: { width: 46, height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.9)', borderWidth: 1.5, borderColor: t.gold },
    msgOn: { backgroundColor: t.gold },
    skipBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.9)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)' },
    pause: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.9)', borderWidth: 1.5, borderColor: t.gold },
    x: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(214,180,106,0.5)' },
    bubble: { maxWidth: '92%', borderRadius: 14, paddingHorizontal: 11, paddingVertical: 7, borderWidth: 1 },
    theirs: { alignSelf: 'flex-start', backgroundColor: 'rgba(16,24,48,0.92)', borderColor: 'rgba(214,180,106,0.35)' },
    mine: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    bubbleText: { color: '#f3ead2', fontSize: 14, lineHeight: 19 },
    done: { color: t.goldBright, fontSize: 12, fontStyle: 'italic', paddingHorizontal: 4 },
    inputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    input: { flex: 1, height: 40, borderRadius: 20, paddingHorizontal: 14, backgroundColor: '#ffffff', borderWidth: 1.5, borderColor: t.gold, color: '#111827', fontSize: 14 },
    send: { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: t.gold },
  }),
);
